import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  ITriggerProps,
  ITriggersTableContent,
} from "../../../../../../@types/Alerts";
import { Dots } from "../../../../../../assets/icons/Dots";
import { IncreaseArrow } from "../../../../../../assets/icons/IncreaseArrow";
import { Button } from "../../../../../../components/Button";
import { CustomIconButton } from "../../../../../../components/CustomIconButton";
import {
  Dropdown,
  DropdownTextItem,
} from "../../../../../../components/Dropdown";
import { Table } from "../../../../../../components/Table";
import { ActionContainer } from "../../../../../../components/TableActionDropdownButton/TableActionDropdownButton.styles";
import { TableSkeleton } from "../../../../../../components/TableSkeleton";
import { getTriggersData } from "../../../../../../hooks/queries/alerts";
import { alertsService } from "../../../../../../services/alertsService";
import {
  formatTriggerMeasurementType,
  formatTriggerNotifications,
  formatTriggerTimeFrame,
} from "../../../../../../utils/triggerLabels";
import {
  formatTriggerSummary,
  getStoredWeightUnit,
} from "../../../../../../utils/triggerSummary";
import { CreateTriggerModal } from "../CreateTriggerModal";
import {
  Container,
  DeleteTriggerContainer,
  ContentContainer,
  SubmitContainer,
  Title,
  CancelButton,
  Field,
  FieldContainer,
} from "./ManageTriggersModal.styles";
import { manageAlertsTableColumns } from "./ManageTriggersTableColumns";

type ManageTriggersModalProps = {
  patientId: number;
  setNotification: (
    notificationMessage: string,
    notificationType: "success" | "warning" | "error"
  ) => void;
};

export function ManageTriggersModal({
  setNotification,
  patientId,
}: ManageTriggersModalProps) {
  const { t } = useTranslation();

  const [isCreateTrigger, setIsCreateTrigger] = useState(false);
  const [isDeleteTrigger, setIsDeleteTrigger] = useState(false);
  const [isEditTrigger, setIsEditTrigger] = useState(false);
  const [isDropDownOpen, setIsDropDownOpen] = useState(false);
  const [measurementAlertIdToEdit, setMeasurementAlertIdToEdit] = useState("");
  const [deleteTriggerData, setDeleteTriggerData] =
    useState<ITriggersTableContent>();
  const [triggerContent, setTriggerContent] = useState<ITriggerProps>();

  const { triggersTableData, isFetching, refetch, isRefetching } =
    getTriggersData(patientId);

  const handleCreateTrigger = () => {
    setIsCreateTrigger(true);
  };

  const handleDeleteTrigger = () => {
    if (deleteTriggerData && deleteTriggerData.measurementAlertId) {
      alertsService
        .deleteTrigger(deleteTriggerData.measurementAlertId)
        .then((deletedSuccessfully) => {
          const { data } = deletedSuccessfully;

          if (data) {
            refetch();
            setNotification(
              t("patientPage_triggers_manage_deleted"),
              "success"
            );
          } else {
            setNotification(
              t("patientPage_triggers_manage_deleteFailed"),
              "error"
            );
          }
        });
    }

    setIsDeleteTrigger(false);
  };

  const handleChildNotification = (
    notificationMessage: string,
    notificationType: "success" | "warning" | "error"
  ) => {
    refetch();

    setNotification(notificationMessage, notificationType);
  };

  const handleCreateTriggerClose = () => {
    setIsCreateTrigger(false);
    setIsEditTrigger(false);
  };

  const parseTriggerToTable = useMemo(() => {
    if (!triggersTableData) return [];

    // EN-5191: ids, not labels — the handler matches on these.
    const options = ["edit", "delete"];

    const handleOnChangeDropdownVisibility = () => {
      setIsDropDownOpen(!isDropDownOpen);
    };

    const editTrigger = (measurementAlertId: string) => {
      alertsService
        .getTriggerByMeasurementAlertId(measurementAlertId)
        .then((trigger) => {
          const { data } = trigger;

          setMeasurementAlertIdToEdit(measurementAlertId);
          setTriggerContent(data);
          setTimeout(() => {
            setIsEditTrigger(true);
          }, 1);
        });
    };

    const deleteTrigger = (triggerData: ITriggersTableContent) => {
      setDeleteTriggerData(triggerData);
      setIsDeleteTrigger(true);
    };

    const handleTriggerText = (triggerText: string) => {
      if (triggerText.includes("+")) {
        const triggerWithoutPlus = triggerText.replace("+", "");

        return (
          <div
            style={{
              display: "flex",
              gap: "2px",
              marginLeft: -4,
            }}
          >
            <IncreaseArrow /> {triggerWithoutPlus}
          </div>
        );
      }

      return triggerText;
    };

    const weightUnit = getStoredWeightUnit();

    return triggersTableData.map((triggerData: ITriggersTableContent) => {
      // EN-5284: the API formats the first three in English, and the summary is stored in
      // English; all four are shown in the user's language, and the delete confirmation shows
      // the same text as the row. Nothing that is saved changes.
      const labels = {
        measurementType: formatTriggerMeasurementType(t, triggerData),
        timeFrame: formatTriggerTimeFrame(t, triggerData) ?? "",
        hcpNotification: formatTriggerNotifications(t, triggerData),
        hcpMessage: formatTriggerSummary(t, triggerData, weightUnit),
      };

      return {
        measurementAlertId: triggerData.measurementAlertId,
        measurementType: labels.measurementType,
        trigger: handleTriggerText(triggerData.trigger as string),
        timeFrame: labels.timeFrame,
        hcpNotification: labels.hcpNotification,
        hcpMessage: labels.hcpMessage,
        created: triggerData.created,
        action: (
          <div>
            <ActionContainer
              className="actionContainer"
              style={{ height: "42px" }}
            >
              <Dropdown
                trigger={
                  <CustomIconButton>
                    <Dots />
                  </CustomIconButton>
                }
                onOpenChange={handleOnChangeDropdownVisibility}
                onValueChange={(val: string) => {
                  if (val === options[0]) {
                    editTrigger(triggerData.measurementAlertId);
                  }
                  if (val === options[1]) {
                    deleteTrigger({ ...triggerData, ...labels });
                  }
                }}
              >
                {options.map((option) => (
                  <DropdownTextItem
                    key={option}
                    value={option}
                    text={t(`patientPage_triggers_manage_${option}`)}
                  />
                ))}
              </Dropdown>
            </ActionContainer>
          </div>
        ),
      } as ITriggersTableContent;
    });
  }, [triggersTableData, t]);

  const handleTable = () => {
    if (isFetching || isRefetching) {
      return <TableSkeleton columnsNumber={6} />;
    }

    return (
      <div style={{ height: "50vh" }}>
        <Table
          data={parseTriggerToTable}
          columns={manageAlertsTableColumns}
          rowPadding
          fontSize={14}
          hasBorder={false}
        />
      </div>
    );
  };

  if (isCreateTrigger || isEditTrigger) {
    return (
      <CreateTriggerModal
        content={isEditTrigger ? triggerContent : undefined}
        isEdit={isEditTrigger}
        measurementAlertId={measurementAlertIdToEdit}
        patientId={patientId}
        setParentNotification={handleChildNotification}
        onClose={handleCreateTriggerClose}
      />
    );
  }

  return !isDeleteTrigger ? (
    <Container>
      <Title>{t("patientPage_triggers_manage_title")}</Title>
      <div
        style={{
          height: "50vh",
          width: "calc(100% + 32px)",
          marginLeft: "-16px",
          fontSize: "12px !important",
        }}
      >
        {handleTable()}
      </div>
      <SubmitContainer>
        <Button
          label={t("patientPage_triggers_manage_create")}
          type="button"
          onClick={handleCreateTrigger}
        />
      </SubmitContainer>
    </Container>
  ) : (
    <DeleteTriggerContainer>
      <Title>{t("patientPage_triggers_manage_deleteTitle")}</Title>
      <ContentContainer>
        <FieldContainer>
          <Field>
            <p style={{ fontWeight: "600" }}>
              {t("patientPage_triggers_manage_type")}
            </p>
            <p>{deleteTriggerData?.measurementType}</p>
          </Field>
          <Field>
            <p style={{ fontWeight: "600" }}>
              {t("patientPage_triggers_manage_trigger")}
            </p>
            <p>{deleteTriggerData?.trigger}</p>
          </Field>
          <Field>
            <p style={{ fontWeight: "600" }}>
              {t("patientPage_triggers_manage_timeFrame")}
            </p>
            <p>{deleteTriggerData?.timeFrame}</p>
          </Field>
          <Field>
            <p style={{ fontWeight: "600" }}>
              {t("patientPage_triggers_manage_hcpNotification")}
            </p>
            <p>{deleteTriggerData?.hcpNotification}</p>
          </Field>
        </FieldContainer>
        <Field>
          <p style={{ fontWeight: "600" }}>
            {t("patientPage_triggers_manage_hcpMessage")}
          </p>
          <p>{deleteTriggerData?.hcpMessage}</p>
        </Field>
      </ContentContainer>
      <SubmitContainer>
        <Button
          label={t("patientPage_triggers_manage_deleteButton")}
          type="button"
          onClick={handleDeleteTrigger}
        />
        <CancelButton onClick={() => setIsDeleteTrigger(false)}>
          {t("common_cancel")}
        </CancelButton>
      </SubmitContainer>
    </DeleteTriggerContainer>
  );
}
